// web/src/components/admin/LogStream.tsx

import { useState, useEffect, useRef, useCallback } from 'react';
import { Terminal, ArrowDown, Clock, Wifi, WifiOff, RefreshCw } from 'react-feather';
import { LogLine } from '../../types/pipeline';

interface LogStreamProps {
  jobId: string;
  startedAt?: string;
  onComplete?: (status: string) => void;
}

interface LogLineWithId extends LogLine {
  id: number;
}

export default function LogStream({ jobId, startedAt, onComplete }: LogStreamProps) {
  const [lines, setLines] = useState<LogLineWithId[]>([]);
  const [connected, setConnected] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [elapsedTime, setElapsedTime] = useState('0:00');
  const [reconnectCount, setReconnectCount] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const lastEventIdRef = useRef<number>(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isCompletedRef = useRef<boolean>(false);

  // Update elapsed time
  useEffect(() => {
    if (!startedAt) return;

    const updateElapsed = () => {
      const start = new Date(startedAt).getTime();
      const now = Date.now();
      const elapsed = Math.floor((now - start) / 1000);
      const minutes = Math.floor(elapsed / 60);
      const seconds = elapsed % 60;
      setElapsedTime(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [startedAt]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (autoScroll && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [lines, autoScroll]);

  // Connect to SSE stream
  const connectToStream = useCallback(() => {
    // Don't reconnect if job is completed
    if (isCompletedRef.current) return;

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    // Clear any pending reconnect
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Build URL with lastEventId for resume
    const url = lastEventIdRef.current > 0
      ? `/api/admin/pipeline/jobs/${jobId}/logs?lastEventId=${lastEventIdRef.current}`
      : `/api/admin/pipeline/jobs/${jobId}/logs`;

    console.log(`[LogStream] Connecting to ${url} (lastEventId: ${lastEventIdRef.current})`);
    const eventSource = new EventSource(url);
    eventSourceRef.current = eventSource;

    eventSource.addEventListener('connected', () => {
      console.log('[LogStream] Connected');
      setConnected(true);
      setReconnectCount(0);
    });

    eventSource.addEventListener('message', (event: MessageEvent) => {
      try {
        const eventId = parseInt(event.lastEventId) || 0;
        const data = JSON.parse(event.data) as LogLine;

        // Only add if we haven't seen this event ID
        if (eventId > lastEventIdRef.current) {
          lastEventIdRef.current = eventId;
          setLines((prev) => [...prev, { ...data, id: eventId }]);
        }
      } catch (error) {
        console.error('[LogStream] Failed to parse log line:', error);
      }
    });

    eventSource.addEventListener('complete', (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[LogStream] Job complete:', data.status);
        isCompletedRef.current = true;
        onComplete?.(data.status);
      } catch (error) {
        console.error('[LogStream] Failed to parse complete event:', error);
      }
      eventSource.close();
      setConnected(false);
    });

    eventSource.addEventListener('error', (_event) => {
      console.log('[LogStream] Connection error, will retry...');
      setConnected(false);

      // Don't retry if job is completed
      if (isCompletedRef.current) return;

      // Exponential backoff: 1s, 2s, 4s, 8s, max 30s
      const delay = Math.min(1000 * Math.pow(2, reconnectCount), 30000);
      console.log(`[LogStream] Reconnecting in ${delay}ms (attempt ${reconnectCount + 1})`);

      setReconnectCount((prev) => prev + 1);
      reconnectTimeoutRef.current = setTimeout(() => {
        connectToStream();
      }, delay);
    });

    eventSource.addEventListener('open', () => {
      console.log('[LogStream] Connection opened');
      setConnected(true);
    });
  }, [jobId, onComplete, reconnectCount]);

  // SSE connection effect
  useEffect(() => {
    connectToStream();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [jobId]); // Only reconnect if jobId changes, not on every connectToStream change

  const toggleAutoScroll = useCallback(() => {
    setAutoScroll((prev) => !prev);
  }, []);

  return (
    <div className="bg-gray-900 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gray-800 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Terminal className="text-green-400" size={18} />
          <span className="text-white font-medium">Pipeline Output</span>
          {connected ? (
            <Wifi className="text-green-400" size={16} />
          ) : (
            <WifiOff className="text-red-400" size={16} />
          )}
          <span className={`text-xs ${connected ? 'text-green-400' : 'text-yellow-400'}`}>
            {connected ? 'Connected' : reconnectCount > 0 ? `Reconnecting (${reconnectCount})...` : 'Connecting...'}
          </span>
          {reconnectCount > 0 && !connected && (
            <RefreshCw className="text-yellow-400 animate-spin" size={14} />
          )}
        </div>

        <div className="flex items-center space-x-4">
          {startedAt && (
            <div className="flex items-center space-x-1 text-gray-400 text-sm">
              <Clock size={14} />
              <span>Elapsed: {elapsedTime}</span>
            </div>
          )}

          <button
            onClick={toggleAutoScroll}
            aria-pressed={autoScroll}
            className={`flex items-center space-x-1 px-2 py-1 rounded text-xs ${
              autoScroll
                ? 'bg-green-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            <ArrowDown size={12} />
            <span>Auto-scroll</span>
          </button>
        </div>
      </div>

      {/* Log content */}
      <div
        ref={containerRef}
        data-testid="log-container"
        className="p-4 h-96 overflow-y-auto font-mono text-sm"
      >
        {lines.length === 0 ? (
          <div className="text-gray-500 text-center py-8">
            Waiting for logs...
          </div>
        ) : (
          lines.map((log) => (
            <div key={log.id} className="text-gray-300 leading-relaxed">
              <span className="text-gray-500 text-xs mr-2">
                {new Date(log.timestamp).toLocaleTimeString()}
              </span>
              <span
                className={
                  log.line.includes('[ERROR]')
                    ? 'text-red-400'
                    : log.line.includes('Completed')
                    ? 'text-green-400'
                    : log.line.includes('Starting')
                    ? 'text-blue-400'
                    : ''
                }
              >
                {log.line}
              </span>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="bg-gray-800 px-4 py-2 text-xs text-gray-500">
        {lines.length} lines
      </div>
    </div>
  );
}
